import { Link } from 'react-router-dom';

// Shared shell for /terms and /privacy. Premium readable layout: serif
// headings (DM Serif Display via font-display), DM Sans body, generous
// line-height, comfortable max-width, sticky minimal Pareto header.
//
// Section / Sub / P / UL / Strong / Code primitives below keep each
// legal page's content terse and consistent so future updates only
// require copying the corresponding markdown changes into the JSX.

export function LegalLayout({ title, effectiveDate, lastUpdated, children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="font-display text-xl text-obsidian-400">
            Pareto
          </Link>
          <nav className="flex items-center gap-5 text-sm">
            <Link to="/terms" className="text-slate-600 hover:text-obsidian-400">
              Terms
            </Link>
            <Link to="/privacy" className="text-slate-600 hover:text-obsidian-400">
              Privacy
            </Link>
            <Link
              to="/login"
              className="inline-flex h-9 items-center justify-center rounded-[8px] bg-teal-600 px-4 text-sm font-semibold text-white shadow-sm shadow-teal-900/20 transition hover:bg-teal-700"
            >
              Sign in
            </Link>
          </nav>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-4 py-10 sm:py-14">
        <h1 className="font-display text-4xl text-obsidian-400 sm:text-5xl">{title}</h1>
        <p className="mt-3 text-sm text-slate-500">
          <strong className="font-semibold text-slate-700">Effective date:</strong>{' '}
          {effectiveDate}
          {lastUpdated ? (
            <>
              {' '}
              · <strong className="font-semibold text-slate-700">Last updated:</strong>{' '}
              {lastUpdated}
            </>
          ) : null}
        </p>

        <aside className="my-7 rounded-[12px] border-l-4 border-amber-400 bg-amber-50 px-5 py-4 text-sm leading-relaxed text-amber-900">
          <strong className="font-semibold">Note from the authors:</strong> This document is a
          working draft. Please consult a licensed attorney before using it in production.
          Anything here is provided as a starting point, not legal advice.
        </aside>

        <div className="space-y-2 text-slate-700">{children}</div>

        <footer className="mt-16 border-t border-slate-200 pt-6 text-center text-xs text-slate-500">
          ©&nbsp;{new Date().getFullYear()} Pareto Labs Inc. ·{' '}
          <Link to="/terms" className="hover:text-obsidian-400">
            Terms
          </Link>{' '}
          ·{' '}
          <Link to="/privacy" className="hover:text-obsidian-400">
            Privacy
          </Link>
        </footer>
      </article>
    </div>
  );
}

export function Section({ number, title, id, children }) {
  return (
    <section id={id} className="mt-10 first:mt-6">
      <h2 className="font-display text-2xl text-obsidian-400 sm:text-3xl">
        {number != null ? `${number}. ` : ''}
        {title}
      </h2>
      <div className="mt-3 space-y-4">{children}</div>
    </section>
  );
}

export function Sub({ title, children }) {
  return (
    <div className="mt-6">
      <h3 className="text-base font-semibold text-obsidian-400">{title}</h3>
      <div className="mt-2 space-y-3">{children}</div>
    </div>
  );
}

export function P({ children }) {
  return <p className="text-[15px] leading-relaxed text-slate-700">{children}</p>;
}

export function UL({ children }) {
  return (
    <ul className="ml-5 list-outside list-disc space-y-2 text-[15px] leading-relaxed text-slate-700 marker:text-slate-400">
      {children}
    </ul>
  );
}

export function Strong({ children }) {
  return <strong className="font-semibold text-obsidian-400">{children}</strong>;
}

export function Code({ children }) {
  return (
    <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[13px] text-obsidian-400">
      {children}
    </code>
  );
}
